import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gray-900 relative overflow-hidden">
        <div className="absolute right-8 top-8 opacity-5 pointer-events-none select-none">
          <i className="ri-settings-3-line text-yellow-400" style={{ fontSize: '200px' }}></i>
        </div>
        <div className="max-w-4xl mx-auto px-4 md:px-8 relative z-10">
          <div className="inline-flex items-center gap-2 border border-yellow-400/40 rounded-full px-4 py-1.5 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-check-line text-yellow-400 text-sm"></i>
            </div>
            <span className="text-yellow-400 text-xs font-bold tracking-widest uppercase">Privacy Policy</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Montserrat', 'Noto Sans JP', sans-serif" }}>
            プライバシーポリシー
          </h1>
          <p className="text-gray-400 text-base leading-relaxed">
            合同会社アテディアは、お客様の個人情報の保護を重要な責務と考え、以下のとおりプライバシーポリシーを定めます。
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1 py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="prose prose-gray max-w-none">

            {/* Article 1 */}
            <section className="mb-10">
              <h4 id="article-1" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-1" className="text-yellow-500 text-sm font-bold mr-1">§1</a>
                個人情報の定義
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                本プライバシーポリシーにおける「個人情報」とは、個人情報の保護に関する法律（以下「個人情報保護法」）に定める個人情報、すなわち、生存する個人に関する情報であって、当該情報に含まれる氏名、生年月日その他の記述等により特定の個人を識別することができるもの（他の情報と容易に照合することができ、それにより特定の個人を識別することができることとなるものを含む）をいいます。
              </p>
            </section>

            {/* Article 2 */}
            <section className="mb-10">
              <h4 id="article-2" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-2" className="text-yellow-500 text-sm font-bold mr-1">§2</a>
                個人情報の収集
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                当社は、以下の場合に個人情報を収集することがあります。
              </p>
              <ul className="list-none flex flex-col gap-2">
                {[
                  'お問い合わせフォームからのご連絡時',
                  'サービスのお申し込み時',
                  'メールマガジンの登録時',
                  'その他、当社サービスのご利用時',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-line text-yellow-500"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Article 3 */}
            <section className="mb-10">
              <h4 id="article-3" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-3" className="text-yellow-500 text-sm font-bold mr-1">§3</a>
                個人情報の利用目的
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                当社は、収集した個人情報を以下の目的のために利用します。
              </p>
              <ul className="list-none flex flex-col gap-2">
                {[
                  'お問い合わせへの回答・対応のため',
                  'サービスの提供・改善のため',
                  '新サービス・キャンペーン等のご案内のため',
                  '法令に基づく対応のため',
                  'その他、上記に付随する目的のため',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-line text-yellow-500"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Article 4 */}
            <section className="mb-10">
              <h4 id="article-4" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-4" className="text-yellow-500 text-sm font-bold mr-1">§4</a>
                個人情報の第三者提供
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                当社は、以下の場合を除き、お客様の個人情報を第三者に提供することはありません。
              </p>
              <ul className="list-none flex flex-col gap-2 mt-3">
                {[
                  'お客様の同意がある場合',
                  '法令に基づく場合',
                  '人の生命、身体または財産の保護のために必要がある場合',
                  '公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-line text-yellow-500"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Article 5 */}
            <section className="mb-10">
              <h4 id="article-5" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-5" className="text-yellow-500 text-sm font-bold mr-1">§5</a>
                個人情報の安全管理
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                当社は、個人情報の漏洩、滅失またはき損の防止その他の個人情報の安全管理のために必要かつ適切な措置を講じます。また、個人情報を取り扱う従業員に対して、必要かつ適切な監督を行います。
              </p>
            </section>

            {/* Article 6 */}
            <section className="mb-10">
              <h4 id="article-6" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-6" className="text-yellow-500 text-sm font-bold mr-1">§6</a>
                Cookieの使用について
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                当社のウェブサイトでは、サービスの向上を目的としてCookieを使用することがあります。Cookieはお客様のブラウザに保存される小さなテキストファイルであり、お客様はブラウザの設定によりCookieの受け取りを拒否することができます。ただし、Cookieを無効にした場合、一部のサービスが正常に機能しない場合があります。
              </p>
            </section>

            {/* Article 7 */}
            <section className="mb-10">
              <h4 id="article-7" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-7" className="text-yellow-500 text-sm font-bold mr-1">§7</a>
                個人情報の開示・訂正・削除
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                お客様は、当社が保有するご自身の個人情報について、開示・訂正・削除を請求することができます。ご請求の際は、お問い合わせフォームよりご連絡ください。当社は、合理的な期間内に対応いたします。
              </p>
            </section>

            {/* Article 8 */}
            <section className="mb-10">
              <h4 id="article-8" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-8" className="text-yellow-500 text-sm font-bold mr-1">§8</a>
                プライバシーポリシーの変更
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                当社は、法令の変更や事業内容の変化に応じて、本プライバシーポリシーを変更することがあります。変更後のプライバシーポリシーは、当ウェブサイトに掲載した時点から効力を生じるものとします。
              </p>
            </section>

            {/* Article 9 */}
            <section className="mb-10">
              <h4 id="article-9" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <a href="#article-9" className="text-yellow-500 text-sm font-bold mr-1">§9</a>
                お問い合わせ窓口
              </h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  個人情報の取り扱いに関するお問い合わせは、以下の窓口までご連絡ください。
                </p>
                <div className="flex flex-col gap-2 text-sm text-gray-700">
                  <div><strong>会社名：</strong>合同会社アテディア</div>
                  <div><strong>所在地：</strong>大阪府大阪市東成区深江北一丁目6-31-601</div>
                  <div><strong>設立：</strong>令和4年9月5日</div>
                  <div>
                    <strong>メール：</strong>
                    <a href="mailto:info@atedia.co.jp" className="text-yellow-600 hover:underline">info@atedia.co.jp</a>
                  </div>
                </div>
              </div>
            </section>

          </div>

          {/* Back button */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-black text-white font-bold px-8 py-3 rounded-full hover:bg-yellow-400 hover:text-black transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              トップページへ戻る
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
